<template>
    <div class="team-header">
        <div>   
            <span>{{ props.team.teamName }}</span>
            <span v-if="state.changed" class="changed-sign">*</span>
        </div>
        <div>
            <DxButton hint="Rename team/Change description" icon="edit" styling-mode="contained" type="normal" @click="onRenameTeam" />
            <DxButton hint="Add member" icon="add" styling-mode="contained" type="normal" @click="onAddMember" />
            <DxButton hint="Save" icon="save" styling-mode="contained" type="normal" @click="onSaveTeam" :disabled="false" />            
            <DxButton hint="Delete team" icon="trash" styling-mode="contained" type="normal" @click="onDeleteTeam" />
        </div>     
    </div>
    <div class="team-description">
        {{ props.team.description }}
    </div>       
    <hr/>
    <div v-for="leader in props.team.teamLead" :key="leader.id" class="team-leaders">
        <TeamMember :is-team-lead="true" :member="leader" @remove="onRemoveLeader"></TeamMember>                  
    </div>
    <hr/>
    <div v-for="member in props.team.members" :key="member.id" class="team-members">
        <TeamMember :is-team-lead="false" :member="member" @remove="onRemoveMember"></TeamMember>
    </div>
    <ConfirmModal
        :modal-id="confirmModalId"
        :text="getConfirmText()"
        :confirm-text="getConfirmButtonText()"
        @confirm="modalConfirmed" @close="modalClosed">
    </ConfirmModal>
    <TeamMemberSelectModal 
        :modal-id="teamMemberSelectModalId"        
        @close="() => vfm.close(teamMemberSelectModalId)" 
        @save="doAddMember"        
        :button-caption="'Add Member'"
        :user="state.addedUser"
        >       
    </TeamMemberSelectModal>
    <EditTeamPropertiesModal 
        :modal-id="teamRenameModalId"        
        @close="() => vfm.close(teamRenameModalId)" 
        :team="props.team">
    </EditTeamPropertiesModal>
</template>

<script lang="ts" setup>
import { reactive } from 'vue';
import { DxButton } from 'devextreme-vue';
import TeamMember from './TeamMember.vue';
import { ServiceTeamItem } from '@/services/entities/ServiceTeams/ServiceTeamItem';
import { UserItem } from '@/services/entities/UserItem';
import { useVfm } from "vue-final-modal";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import TeamMemberSelectModal from './dialogs/TeamMemberSelectModal.vue';
import EditTeamPropertiesModal from './dialogs/EditTeamPropertiesModal.vue';
import { showWarning } from '@/toastification';
import { useServiceTeamsStore } from '@/stores/serviceTeams';

const vfm = useVfm();
const confirmModalId = Symbol("confirmModalId");
const teamMemberSelectModalId = Symbol("teamMemberSelectModalId");
const teamRenameModalId = Symbol("teamRenameModalId");
const teamsStore = useServiceTeamsStore();

const props = defineProps({
    team: { Type: {} as ServiceTeamItem, default: {} as ServiceTeamItem },
});

const emit = defineEmits(["reload"]);

const state = reactive({
   isSaving: false,
   isDeleting: false, 
   addedUser: {} as UserItem,
   addAsLeader: false,
   changed: false,
});

function getConfirmText() {
    if (state.isSaving) {
        return "Are you sure you want to save changes to this service team?";
    } else if (state.isDeleting) {
        return "Are you sure you want to delete this service team?";
    }
    return "";
}

function getConfirmButtonText() {
    if (state.isSaving) {
        return "Save changes";
    } else if (state.isDeleting) {
        return "Delete team";
    }
    return "Confirm";
}

async function modalConfirmed() {
    if (state.isSaving) {
        const team = teamsStore.getServiceTeams.find(t => t.id === props.team.id);
        if (team) {            
            await teamsStore.updateServiceTeam(props.team);
        } else {
            await teamsStore.createServiceTeam(props.team);
        }
    } else if (state.isDeleting) {
        await teamsStore.deleteServiceTeam(props.team.id);
    }
    emit("reload", props.team.service.id);
    vfm.close(confirmModalId);
    
}



function modalClosed() {
    state.isSaving = false;
    state.isDeleting = false;
    vfm.close(confirmModalId);
}   

function onSaveTeam() {
    state.isSaving = true;
    state.isDeleting = false;
    vfm.open(confirmModalId);    
}

function onRenameTeam() {
    vfm.open(teamRenameModalId);
}

function onAddMember() {
    state.addAsLeader = false;
    state.addedUser = {} as UserItem;
    vfm.open(teamMemberSelectModalId);
}

function onDeleteTeam() {
    state.isDeleting = true;
    state.isSaving = false; 
    vfm.open(confirmModalId);
}

function onRemoveLeader(leader: UserItem) {
    if (leader) {
        let indx = props.team.teamLead.indexOf(leader);
        if (indx > -1) {
            props.team.teamLead.splice(indx, 1)
        }
    }    
}

function onRemoveMember(member: UserItem) {
    if (member) {
        let indx = props.team.members.indexOf(member);
        if (indx > -1) {
            props.team.members.splice(indx, 1)
        }
    }
}

function doDeleteMember(member: UserItem) {

}

function doAddMember(addedUser: UserItem, asLeader: boolean) {
    if (addedUser) {
        if (isAllreadyInTeam(addedUser)) {
            showWarning(`${addedUser.fullName} is already in team`);
            return;
        }
        if (asLeader) {
            if (props.team.teamLead) 
                props.team.teamLead.push(addedUser)
            else
                props.team.teamLead = [addedUser];
            
        } else {
            if (props.team.members)
                props.team.members.push(addedUser);
            else
                props.team.members = [addedUser];
        }        
    }
    vfm.close(teamMemberSelectModalId);
}

function isAllreadyInTeam(user: UserItem): boolean {
    return (props.team.members?.some((u) => u.id == user.id) ||
            props.team.teamLead?.some((l) => l.id == user.id));
}


</script>
<style lang="scss" scoped>
.team-header {
    display: flex;
    flex-direction: row;
    padding-left: 8px;
    font-weight: bold;
    font-size: normal;
    justify-content: space-between;    
}
.team-members .team-leaders {
    display: flex;
    flex-direction: column;
    padding: 4px 8px 0px 16px;
}
.changed-sign {
    color: red;
    margin-left: 4px;
    font-weight: bold;
}
.team-description {
    padding: 8px 16px;
    font-style: italic;
    font-size: smaller;
}
</style>